import { useState } from 'react';
import type { CategoryGrowthItem, CategoryData } from '../../../hooks/useReports';

interface Props {
  categoryGrowth: CategoryGrowthItem[];
  expensesByCategory: CategoryData[];
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const CategoryInsights = ({ categoryGrowth, expensesByCategory, loading }: Props) => {
  const [tab, setTab] = useState<'peso' | 'crescimento' | 'pendentes' | 'recorrentes'>('peso');

  if (loading) return (
    <div className="space-y-3">
      {[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}
    </div>
  );

  const sorted = {
    peso: [...categoryGrowth].sort((a, b) => b.currentPeriod - a.currentPeriod),
    crescimento: [...categoryGrowth].filter(c => c.growth > 0 && c.prevPeriod > 0).sort((a, b) => b.growth - a.growth),
    pendentes: [...categoryGrowth].filter(c => c.isPending > 0).sort((a, b) => b.isPending - a.isPending),
    recorrentes: categoryGrowth.filter(c => c.isRecurrent),
  };

  const maxCurrent = Math.max(...categoryGrowth.map(c => c.currentPeriod), 1);

  const tabs = [
    { key: 'peso' as const, label: 'Por peso', icon: 'ri-weight-line' },
    { key: 'crescimento' as const, label: 'Que cresceram', icon: 'ri-arrow-up-circle-line' },
    { key: 'pendentes' as const, label: 'Com pendências', icon: 'ri-time-line' },
    { key: 'recorrentes' as const, label: 'Recorrentes', icon: 'ri-repeat-line' },
  ];

  const items = sorted[tab];

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
      <div>
        <h3 className="text-[15px] font-700 text-gray-900">Análise por Categoria</h3>
        <p className="text-[12px] text-gray-400 mt-0.5">Entenda onde seu dinheiro está sendo gasto e o que mudou</p>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-600 cursor-pointer whitespace-nowrap transition-all ${
              tab === t.key ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={`${t.icon} text-[13px]`} />
            {t.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px] gap-2">
          <i className="ri-pie-chart-line text-2xl text-gray-200" />
          Sem dados para esta análise no período
        </div>
      ) : (
        <div className="space-y-3">
          {items.slice(0, 8).map((cat, idx) => {
            const barWidth = tab === 'peso'
              ? Math.round((cat.currentPeriod / maxCurrent) * 100)
              : tab === 'crescimento'
              ? Math.min(100, Math.round(cat.growth))
              : tab === 'pendentes'
              ? 100
              : 100;

            const growthLabel = cat.prevPeriod > 0
              ? (cat.growth > 0 ? `+${Math.round(cat.growth)}% vs. anterior` : `${Math.round(cat.growth)}% vs. anterior`)
              : 'Sem histórico anterior';

            return (
              <div key={cat.category}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                    <span className="text-[12px] font-600 text-gray-800">{cat.category}</span>
                    {cat.isRecurrent && (
                      <span className="text-[9px] bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded-full font-600">
                        <i className="ri-repeat-line mr-0.5" />Rec.
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    {tab === 'crescimento' && cat.prevPeriod > 0 && (
                      <span className={`text-[11px] font-600 ${cat.growth > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                        {cat.growth > 0 ? '+' : ''}{Math.round(cat.growth)}%
                      </span>
                    )}
                    {tab === 'pendentes' && (
                      <span className="text-[11px] text-amber-600 font-600">{fmt(cat.isPending)} pendente</span>
                    )}
                    <span className="text-[12px] font-700 text-gray-800">{fmt(cat.currentPeriod)}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${barWidth}%`, backgroundColor: cat.color }} />
                </div>
                {tab === 'crescimento' && (
                  <p className="text-[10px] text-gray-400 mt-0.5">{growthLabel} · Anterior: {fmt(cat.prevPeriod)}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Top 3 highlight */}
      {tab === 'peso' && expensesByCategory.length > 0 && (
        <div className="pt-3 border-t border-gray-100">
          <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wider mb-2">Top 3 categorias</p>
          <div className="flex gap-2 flex-wrap">
            {expensesByCategory.slice(0, 3).map((cat, i) => (
              <div key={cat.category} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
                <span className="text-[11px] font-600 text-gray-500">#{i + 1}</span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                <span className="text-[11px] font-600 text-gray-700">{cat.category}</span>
                <span className="text-[11px] text-gray-500">{cat.percent}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
